import React from 'react';
import {
  Shield,
  Lock,
  Server,
  EyeOff,
  Key,
  CheckCircle,
  XCircle,
  HardDrive,
  ShieldCheck,
  Bug,
  Monitor,
  Users,
  AlertTriangle,
  Fingerprint,
  Clock,
  Zap,
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const TrustSecurity = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.3 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.2 });

  // Core trust principles
  const trustPrinciples = [
    {
      icon: EyeOff,
      title: 'Zero Data Access',
      description: 'We NEVER see your trading data, strategies, or positions.',
    },
    {
      icon: HardDrive,
      title: 'On Your Infrastructure',
      description: 'Runs entirely on your hardware. No cloud dependency.',
    },
    {
      icon: Key,
      title: 'Your IP, Your Control',
      description: 'Your strategies and algorithms stay with you.',
    },
    {
      icon: Lock,
      title: 'Machine-Bound License',
      description: 'Cryptographically bound to your specific hardware.',
    },
  ];

  // What we DON'T do
  const whatWeDoNot = [
    'Access your trading strategies',
    'Store your market data',
    'Log your trading decisions',
    'Monitor your order flow',
  ];

  // What you control
  const whatYouControl = [
    'All trading logic',
    'Complete data flow',
    'Network configuration',
    'Risk parameters',
  ];

  // Enterprise IP Protection Features
  const ipProtectionFeatures = [
    {
      icon: Bug,
      title: 'Anti-Debug (5 Methods)',
      items: ['Timing anomaly detection', 'ptrace/TracerPid check', 'Parent process scan'],
      borderClass: 'border-red-500/20 hover:border-red-500/40',
      bgClass: 'bg-red-500/10',
      iconClass: 'text-red-400',
      dotClass: 'bg-red-400',
    },
    {
      icon: Monitor,
      title: 'VM Detection (8 Types)',
      items: ['VMware/VirtualBox/Hyper-V', 'KVM/Xen detection', 'Docker/K8s containers'],
      borderClass: 'border-orange-500/20 hover:border-orange-500/40',
      bgClass: 'bg-orange-500/10',
      iconClass: 'text-orange-400',
      dotClass: 'bg-orange-400',
    },
    {
      icon: Fingerprint,
      title: 'Hardware-Bound License',
      items: ['CPU ID + MAC binding', 'Disk serial verify', 'RSA signature check'],
      borderClass: 'border-purple-500/20 hover:border-purple-500/40',
      bgClass: 'bg-purple-500/10',
      iconClass: 'text-purple-400',
      dotClass: 'bg-purple-400',
    },
    {
      icon: Users,
      title: 'Floating License Server',
      items: ['Multi-seat license pools', 'Heartbeat monitoring', 'Session management'],
      borderClass: 'border-blue-500/20 hover:border-blue-500/40',
      bgClass: 'bg-blue-500/10',
      iconClass: 'text-blue-400',
      dotClass: 'bg-blue-400',
    },
  ];

  // Emergency Controls
  const emergencyControls = [
    { icon: AlertTriangle, text: 'Customer Kill Switch', desc: 'File-based halt in <5 seconds' },
    { icon: Clock, text: 'Circuit Breakers', desc: 'Configurable cooldown periods' },
    { icon: Zap, text: 'Instant Resume', desc: 'Delete stop file to continue' },
  ];

  return (
    <section id="trust-security" className="py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
            <ShieldCheck className="w-5 h-5 text-green-400" />
            <span className="text-sm font-medium text-green-400">Your Data Stays Yours</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Trust & <span className="text-gradient">Security</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Built for firms who demand absolute confidentiality.
          </p>
        </div>

        <div
          ref={contentRef}
          className={`transition-all duration-700 ${
            contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Main Trust Statement */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="p-8 rounded-2xl bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-teal-500/10 border border-green-500/20 text-center">
              <Shield className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Complete Data Sovereignty
              </h3>
              <p className="text-muted-foreground">
                We provide the engine. <span className="text-green-400 font-semibold">You own everything else.</span>
              </p>
            </div>
          </div>

          {/* Trust Principles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-12">
            {trustPrinciples.map((principle, index) => (
              <div
                key={index}
                className="p-5 rounded-xl bg-card/50 border border-border text-center"
              >
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center mx-auto mb-3">
                  <principle.icon className="w-5 h-5 text-green-400" />
                </div>
                <h4 className="text-sm font-semibold text-white mb-1">{principle.title}</h4>
                <p className="text-xs text-muted-foreground">{principle.description}</p>
              </div>
            ))}
          </div>

          {/* What We Do / Don't Do */}
          <div className="max-w-3xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* What We DON'T Do */}
              <div className="p-5 rounded-xl bg-red-500/5 border border-red-500/20">
                <div className="flex items-center gap-2 mb-4">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <h4 className="font-semibold text-red-400">We NEVER</h4>
                </div>
                <ul className="space-y-2">
                  {whatWeDoNot.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* What You Control */}
              <div className="p-5 rounded-xl bg-green-500/5 border border-green-500/20">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <h4 className="font-semibold text-green-400">YOU Control</h4>
                </div>
                <ul className="space-y-2">
                  {whatYouControl.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Enterprise IP Protection - NEW */}
          <div className="max-w-5xl mx-auto mt-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">
                Enterprise IP Protection
              </h3>
              <p className="text-sm text-muted-foreground">
                Multi-layer security to protect your investment and intellectual property
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {ipProtectionFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-xl bg-card/50 border ${feature.borderClass} transition-colors`}
                >
                  <div className={`w-8 h-8 rounded-lg ${feature.bgClass} flex items-center justify-center mb-3`}>
                    <feature.icon className={`w-4 h-4 ${feature.iconClass}`} />
                  </div>
                  <h4 className="text-sm font-semibold text-white mb-2">{feature.title}</h4>
                  <ul className="space-y-1">
                    {feature.items.map((item, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <span className={`w-1 h-1 rounded-full ${feature.dotClass} flex-shrink-0`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Emergency Controls */}
            <div className="p-6 rounded-xl bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <h4 className="font-semibold text-amber-400">Emergency Controls — YOU Own the Kill Switch</h4>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {emergencyControls.map((control, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <control.icon className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{control.text}</p>
                      <p className="text-xs text-muted-foreground">{control.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-black/30 font-mono text-xs text-amber-400/80">
                <span className="text-muted-foreground"># Halt trading instantly:</span><br />
                echo "STOP" &gt; config/emergency_stop.txt
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
            >
              <Shield className="w-4 h-4" />
              Request Security Whitepaper
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSecurity;
